import React, { useMemo } from 'react';
import { useIntl } from "react-intl";
import { getPageFromList, Member, MemberPageParam } from '@/model';
import { compareDate } from '@/util/dateUtil';
import { useMembers } from '@/query/member';
import { useAgentId, useModal, usePageParam } from "@/hook";
import Button from "@/component/Button";
import { SearchBar } from '@/component/input';
import PaginationBar from '@/component/PaginationBar';
import { AddIcon } from "@/assets/svgs/icons";

import MemberTable from "./component/MemberTable";
import MemberAddModal, { MEMBER_ADD_MODAL_ID } from "./component/MemberAddModal";
import MemberModifyModal from './component/MemberModifyModal';


const MEMBER_PAGE_SIZE = 15;

const MemberView: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const agentId = useAgentId();

    const { param, set: setParam } = usePageParam<MemberPageParam>({
        page: 0,
        size: MEMBER_PAGE_SIZE,
        sortKey: "member_type",
        order: "asc",
    });
    
    const { data: members = [] } = useMembers(agentId);

    const memberPage = useMemo(() => {
        const filter = (member: Member) => {
            const keyword = (param?.keyword ?? "").toLowerCase();

            if (!keyword) {
                return true;
            }
            const { username, nickname, department = "" } = member.user;
            return (
                username.toLowerCase().includes(keyword)
                || nickname.toLowerCase().includes(keyword)
                || department.toLowerCase().includes(keyword)
            );
        };

        const compare = (a: Member, b: Member) => {
            let diff = 0;

            if (param?.sortKey === "member_type") {
                if (a.is_admin !== b.is_admin) {
                    diff = a.is_admin ? -1 : 1;
                } else {
                    const rolesA = a.roles.map(role => role.description).join(", ");
                    const rolesB = b.roles.map(role => role.description).join(", ");
                    diff = rolesA.localeCompare(rolesB);
                }
            } else if (param?.sortKey === "department") {
                if (!a.user.department || !b.user.department) {
                    diff = a.user.department ? -1 : 1;
                } else {
                    diff = a.user.department.localeCompare(b.user.department);
                }
            }
            
            if (diff === 0 && a.updated_at && b.updated_at) {
                diff = compareDate(a.updated_at, b.updated_at);
            }
            return diff;
        };

        let sorted = [...members].filter(filter).sort(compare);
        if (param?.order === "desc") {
            sorted = sorted.reverse();
        }

        return getPageFromList(sorted, param?.page, param?.size);
    }, [members, param]);

    const onSort = (key: MemberPageParam["sortKey"]) => {
        if (param?.sortKey === key) {
            setParam({
                order: param.order === "asc" ? "desc" : "asc",
            });
        } else {
            setParam({
                sortKey: key,
            });
        }
    };

    return (<>
        <MemberAddModal/>
        <MemberModifyModal />

        <div className="agent-content-layout flex flex-col gap-3">
            <div className="search-bar-container w-md-page">
                <SearchBar lg width={680}
                            placeholder={intl.formatMessage({ id: "i12021" })}
                            defaultValue={param?.keyword}
                            onSearch={keyword => setParam({ keyword: keyword })}
                />
                <div className="search-bar-container-btn-group">
                    <Button variant="text"
                            onClick={() => modal.open(MEMBER_ADD_MODAL_ID)}
                    >
                        <AddIcon className="size-3.5"/>
                        {intl.formatMessage({ id: "i12002" })}
                    </Button>
                </div>
            </div>

            <div className="table-pagination-container w-md-page">
                <MemberTable keyword={param?.keyword} 
                             members={memberPage.content}
                             sortKey={param?.sortKey}
                             sortOrder={param?.order}
                             onSort={onSort}
                />

                <PaginationBar pageData={memberPage}
                               onClickPage={page => setParam({ page })}
                />
            </div>
        </div>
    </>);
};

export default MemberView;
